/* eslint-disable prefer-destructuring */
import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { TextField, MenuItem, InputAdornment, Button } from '@material-ui/core';
// import { toJS } from 'mobx';
import SimpleSelect from './SimpleSelect';
import { FormRow, ErrorText } from '../styles/styles';
import { MenuList, filterOptions } from './ReactSelectOverrides';
import { safeEval } from './UtilFunctions';
import { useRootStore } from '../../stores/BridgeProvider';
import { CustomCreatableSelect } from './CustomCreatableSelect';
import RenderPropModal from '../dealMapper/common/RenderPropModal';
import permissionCheck from './PermCheck';

const initialValueConstructor = {
  string: '',
  date: format(new Date(), 'YYYY-MM-DD'),
  enum: 0,
  dollar: '',
  contact: null,
  agent: null,
  tranCategory: '',
};

export const initialValueGenerator = valueArray =>
  valueArray.reduce((result, { name, type, enumOptions }) => {
    result[name] = initialValueConstructor[type];
    // example: statusName select
    if (type === 'enum' && name.includes('Name')) result[name] = enumOptions[0];
    if (['contact', 'agent'].includes(type)) result[`${name}Object`] = null;
    return result;
  }, {});

const ContactSelect = ({ fieldObj, formikProps, isAgent, index }) => {
  const { dataStore, authStore, orgStore } = useRootStore();
  const [isLoading, setLoading] = useState(true);
  const [isCreating, setCreating] = useState(false);
  const [agent, setAgent] = useState({});
  const contacts = dataStore.contactArray;

  const isManager = permissionCheck('DEAL_S');

  const { userContactId } = orgStore;
  useEffect(() => {
    const initialize = async () => {
      const requests = [];
      if (contacts.length === 0) {
        requests.push(dataStore.retrieveContacts());
      }
      await Promise.all(requests);
      setLoading(false);
    };
    initialize();
  }, []);
  const { name, displayName, required } = fieldObj;
  const { values, errors, touched, setFieldValue } = formikProps;
  const showError = val => errors[val] && touched[val];
  const handleCreateContact = async inputValue => {
    setCreating(true);
    try {
      const { data: contactData } = await authStore.request.post(
        `${process.env.REACT_APP_API}/api/contact`,
        {
          Name: inputValue,
          IsAgent: isAgent ? 1 : 0,
        },
      );
      const { id: cId } = contactData;
      await dataStore.retrieveContacts();
      const contact = dataStore.getContactFromId(cId);
      setFieldValue(`${name}Object`, contact);
      setFieldValue(name, contact.Name);
    } catch (e) {
      console.error(e);
    }
    setCreating(false);
  };

  const handleChange = val => {
    setFieldValue(`${name}Object`, val);
    setFieldValue(
      name,
      safeEval(() => val.Name),
      null,
    );
  };

  const handleAgentChange = (handleOpen, val) => {
    const isNotUserContact = safeEval(
      () => userContactId !== val.ContactId,
      true,
    );
    if (val && !isManager && isNotUserContact) {
      setAgent(val);
      handleOpen();
    } else {
      handleChange(val);
    }
  };
  return (
    <div
      style={{
        width: '100%',
        zIndex: 1201 - index,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <RenderPropModal
        modalStyle={{ padding: '1.1rem' }}
        cardDisplay={({ handleClose }) => (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div>
              You’ve selected an agent not associated with your account. When
              this deal is created you will not be able to see it.
            </div>
            <div style={{ marginTop: '10px' }}>
              <Button
                color="secondary"
                onClick={() => {
                  handleChange(agent);
                  setAgent({});
                  handleClose();
                }}
              >
                Proceed
              </Button>
              <Button
                color="primary"
                onClick={() => {
                  setAgent({});
                  handleClose();
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      >
        {({ handleOpen }) => (
          <CustomCreatableSelect
            isDisabled={isLoading || isCreating}
            isSearchable
            isClearable
            placeholder={required ? `${displayName} *` : displayName}
            components={{ MenuList }}
            options={contacts.sort((a, b) => {
              const agentCount = b.IsAgent - a.IsAgent;
              if (agentCount) return agentCount;
              return a.Name > b.Name ? 1 : -1;
            })}
            getOptionLabel={option => option.Name}
            getOptionValue={option => option.Name}
            isValidNewOption={(inputValue, x, selectOptions) =>
              selectOptions.findIndex(option => option.Name === inputValue) < 0
            }
            formatCreateLabel={inputValue => `Create "${inputValue}"`}
            getNewOptionData={(inputValue, optionLabel) => ({
              Name: optionLabel,
              ContactId: inputValue,
              __isNew__: true,
            })}
            value={values[`${name}Object`]}
            filterOption={filterOptions}
            isLoading={isCreating}
            onCreateOption={handleCreateContact}
            onChange={val =>
              isAgent ? handleAgentChange(handleOpen, val) : handleChange(val)
            }
          />
        )}
      </RenderPropModal>
      {showError(name) && <ErrorText>{errors[name]}</ErrorText>}
    </div>
  );
};

const TranCategorySelect = ({ fieldObj, formikProps }) => {
  const { dataStore } = useRootStore();
  const [isLoading, setLoading] = useState(true);
  const { name, displayName, required } = fieldObj;
  const { values, handleChange } = formikProps;
  const transactionCategories = dataStore.validTransactionCategoryArray;
  useEffect(() => {
    const initialize = async () => {
      const requests = [];
      if (transactionCategories.length === 0) {
        requests.push(dataStore.retrieveTransactionCategories());
      }
      await Promise.all(requests);
      setLoading(false);
    };
    initialize();
  }, []);
  return (
    <SimpleSelect
      label={displayName}
      handleChange={handleChange}
      value={values[name]}
      name={name}
      selectProps={{ disabled: isLoading, required }}
    >
      <MenuItem value="">
        <em>None</em>
      </MenuItem>
      {transactionCategories.map(({ TranCategoryId, Code }) => (
        <MenuItem key={TranCategoryId} value={TranCategoryId}>
          {Code}
        </MenuItem>
      ))}
    </SimpleSelect>
  );
};

export const formFieldGenerator = props => {
  const { fieldObj, formikProps } = props;
  const { name, type, required, displayName, enumOptions, width } = fieldObj;
  const { values, errors, touched, handleChange, handleBlur } = formikProps;
  const showError = val => errors[val] && touched[val];
  const fieldProps = {
    key: name,
    name,
    label: displayName,
    value: values[name],
    onChange: handleChange,
    onBlur: handleBlur,
    margin: 'normal',
    variant: 'outlined',
    fullWidth: true,
    // style: { display: 'inline-block', width: width ? `${width}%` : '100%' },
    error: showError(name),
    required,
  };

  // console.log(fieldProps);
  switch (type) {
    case 'string':
      return <TextField {...fieldProps} />;
    case 'dollar':
      return (
        <TextField
          style={{ display: 'flex' }}
          {...fieldProps}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
      );
    case 'date':
      return (
        <TextField
          {...fieldProps}
          id={name}
          type="date"
          style={{ display: 'flex' }}
          InputLabelProps={{
            shrink: true,
          }}
        />
      );
    case 'enum':
      return (
        <FormRow style={{ flexDirection: 'column' }} key={name}>
          <SimpleSelect
            label={displayName}
            handleChange={handleChange}
            value={values[name]}
            name={name}
          >
            {enumOptions.map(value => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </SimpleSelect>
          {showError(name) && <ErrorText>{errors[name]}</ErrorText>}
        </FormRow>
      );
    case 'contact':
      return <ContactSelect key={name} {...props} />;
    case 'agent':
      return <ContactSelect key={name} isAgent {...props} />;
    case 'tranCategory':
      return (
        <FormRow style={{ flexDirection: 'column' }} key={name}>
          <TranCategorySelect {...props} />
          {showError(name) && <ErrorText>{errors[name]}</ErrorText>}
        </FormRow>
      );
    default:
      return null;
  }
};
